import { format, parseISO, isValid, formatDistanceToNow } from "date-fns"
import { utc, UTCDate } from "@date-fns/utc"
import type { IValueLabel } from "@apis/shared/types"

export const formatDate = (
  date: string | Date | undefined | null,
  formatStr: string = "MM/dd/yyyy",
): string | undefined | null => {
  if (date == null) return undefined

  const dateObj =
    typeof date === "string" ? parseISO(date, { in: utc }) : new UTCDate(date)
  return isValid(dateObj) ? format(dateObj, formatStr) : null
}

export const timeAgo = (
  dateString: string | undefined | null,
): string | undefined | null => {
  if (dateString == null) return undefined

  const dateObj = parseISO(dateString)
  return isValid(dateObj)
    ? formatDistanceToNow(dateObj, { addSuffix: true })
    : null
}

export const formatRate = (
  rate: string | number | undefined | null,
  rateUnit: string | undefined,
): string | null => {
  if (!rate || !rateUnit) return null

  const numericRate = typeof rate === "string" ? parseFloat(rate) : rate

  return `$${numericRate} / ${rateUnit}`
}

export const parseCurrency = (value: string) => {
  if (typeof value !== "string") return NaN

  // Remove currency symbols, commas, and spaces
  const cleaned = value.replace(/[^0-9.-]+/g, "")

  return parseFloat(cleaned)
}

export const removeEndingPunctuation = (string: string) => {
  return string.replace(/[!\.]/, "")
}

/**
 * Sorts an array of objects by a date field using date-fns
 * @param data - Array of objects to sort
 * @param dateField - Name of the date field to sort by
 * @param order - Sort order: 'asc' for ascending, 'desc' for descending (default: 'desc')
 * @returns Sorted array
 */
export const sortByDate = <T>(
  data: T[],
  dateField: keyof T,
  order: "asc" | "desc" = "desc",
): T[] => {
  return [...data].sort((a, b) => {
    const dateA = parseISO(a[dateField] as string)
    const dateB = parseISO(b[dateField] as string)

    if (order === "asc") {
      return dateA.getTime() - dateB.getTime()
    } else {
      return dateB.getTime() - dateA.getTime()
    }
  })
}

export const formatCurrency = (amount: unknown, digits: number = 0) => {
  const amountNumber = typeof amount === "number" ? amount : Number(amount) || 0
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: digits,
  }).format(amountNumber)
}

export const serializeDataToValueLabel = <T, K extends keyof T>(
  data: T[],
  valueKey: K extends keyof T ? (T[K] extends string ? K : never) : never,
  labelKey?: K extends keyof T ? (T[K] extends string ? K : never) : never,
  sort: boolean = true,
): IValueLabel[] => {
  const mapped = data
    .map((item) => ({
      value: item[valueKey] as string,
      label: item[labelKey ?? valueKey] as string,
    }))
    .filter((item) => item.value && item.label)

  return sort ? sortLabels(mapped) : mapped
}

export const sortLabels = (data: IValueLabel[]): IValueLabel[] => {
  return data.sort((a, b) => a.label.localeCompare(b.label))
}

export const getUniqueValueLabels = (data: IValueLabel[]): IValueLabel[] => {
  const map = new Map(data.map((item) => [item.value, item]))
  return sortLabels(Array.from(map.values()))
}
